import { create } from "zustand";

const COOKIE_CONSENT_PREFIX = "cookie-consent-";
export const POSTHOG_COOKIE_KEY = "ph_phc_*_posthog";

type TCookieConsent = {
  hasConsented: boolean;
  cookies: Record<string, boolean>;
};

function getConsentKey(userId: string | number): string {
  return `${COOKIE_CONSENT_PREFIX}${userId}`;
}

function getStoredConsent(userId: string | number | undefined): TCookieConsent | null {
  if (!userId) return null;
  try {
    const stored = localStorage.getItem(getConsentKey(userId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveConsent(userId: string | number, consent: TCookieConsent): void {
  localStorage.setItem(getConsentKey(userId), JSON.stringify(consent));
}

export function isCookieAllowed(cookieKey: string, userId: string | number | undefined): boolean {
  const stored = getStoredConsent(userId);
  return stored?.cookies?.[cookieKey] ?? true;
}

type TCookieConsentState = {
  showConsentModal: boolean;
  showConfigureView: boolean;
  userId: string | number | undefined;
  cookies: Record<string, boolean>;
  openModal: () => void;
  openConfigureModal: () => void;
  closeModal: () => void;
  setUserId: (userId: string | number) => void;
  setCookieEnabled: (key: string, enabled: boolean) => void;
  savePreferences: (cookies: Record<string, boolean>) => void;
  hasGivenConsent: () => boolean;
};

export const useCookieConsentStore = create<TCookieConsentState>((set, get) => ({
  showConsentModal: false,
  showConfigureView: false,
  userId: undefined,
  cookies: { [POSTHOG_COOKIE_KEY]: true },
  openModal: () => set({ showConsentModal: true, showConfigureView: false }),
  openConfigureModal: () => set({ showConsentModal: true, showConfigureView: true }),
  closeModal: () => set({ showConsentModal: false, showConfigureView: false }),
  setUserId: (userId) => {
    const stored = getStoredConsent(userId);
    set({
      userId,
      cookies: stored?.cookies ?? { [POSTHOG_COOKIE_KEY]: true }
    });
  },
  setCookieEnabled: (key, enabled) =>
    set((state) => ({ cookies: { ...state.cookies, [key]: enabled } })),
  savePreferences: (cookies) => {
    const { userId } = get();
    if (userId) saveConsent(userId, { hasConsented: true, cookies });
    set({ cookies });
  },
  hasGivenConsent: () => {
    const { userId } = get();
    const stored = getStoredConsent(userId);
    return stored?.hasConsented ?? false;
  }
}));
